import { Component, Input } from '@angular/core';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';

// Interactive "Value Rise" widget for the HSV Value post. Kept deliberately small: a
// hue slider picks the color, one dial drives both models, and two result swatches
// show where each lands. Push the dial to the top and the HSV swatch settles on the
// pure color while the HSL swatch goes all the way to white, which is the whole point
// of the post. Labels and hex sit below each swatch so the color surface stays clean.
// The post marks the mount point with <!--HSV-START-->...<!--HSV-END--> around a static
// fallback (kept for crawlers and no-JS readers); BlogPostPageComponent swaps this live
// component in. Same design language as the value-ladder / saturation-wheel widgets.
@Component( {
  selector: 'app-value-rise',
  templateUrl: './value-rise.component.html',
  styleUrls: [ './value-rise.component.scss' ],
} )
export class ValueRiseComponent {

  @Input() heading = 'Try It Yourself';

  // The hue and saturation of the color under study, plus the dial level (0..100) read
  // as V on the HSV swatch and as L on the HSL one. Hue, saturation and the starting
  // level all come from the post's own color via [hex], so the widget opens on exactly
  // that color instead of a fixed red. Defaults to the Value post's red until bound.
  hue = 359;
  sat = 74;
  level = 98;
  private _hex = '#F94144';

  // The post passes its color here ([hex]="post.relatedColor"); the widget opens on it.
  @Input() set hex( value: string ) {
    const rgb = this.parseHex( value );
    if ( !rgb ) {
      return;
    }
    this._hex = '#' + String( value ).replace( /^#/, '' ).toUpperCase();
    const [ h, s, v ] = this.rgbToHsv( rgb );
    this.hue = h;
    this.sat = s;
    this.level = v;
  }
  get hex(): string {
    return this._hex;
  }

  private tracked = false;

  constructor( private analyticsService: AnalyticsService ) {
  }

  onHue( value: string ): void {
    this.hue = this.clamp( parseInt( value, 10 ), 0, 359 );
    this.track();
  }

  onLevel( value: string ): void {
    this.level = this.clamp( parseInt( value, 10 ), 0, 100 );
    this.track();
  }

  // HSV: the dial read as Value. At full saturation it walks black up to the pure hue.
  get hsvHex(): string {
    return this.hsvToHex( this.hue, this.sat, this.level );
  }

  // HSL: the same dial read as Lightness. It walks black up through the pure hue and
  // on to white, so the top of the dial is white, not the color.
  get hslHex(): string {
    return this.hslToHex( this.hue, this.sat, this.level );
  }

  // The pure, fully bright version of the chosen hue. Used to tint the slider thumbs
  // so the controls speak in the selected hue.
  get hueColor(): string {
    return this.hsvToHex( this.hue, 100, 100 );
  }

  private track(): void {
    if ( this.tracked ) {
      return;
    }
    this.tracked = true;
    this.analyticsService.logEvent( 'Blog Widget Used', { widget: 'value-rise' } );
  }

  private clamp( v: number, lo: number, hi: number ): number {
    if ( isNaN( v ) ) {
      return lo;
    }
    return Math.max( lo, Math.min( hi, v ) );
  }

  // Parse a 6-digit hex (with or without #) into [r, g, b], or null when invalid.
  private parseHex( text: string ): [ number, number, number ] | null {
    const h = String( text || '' ).trim().replace( /^#/, '' );
    if ( !/^[0-9a-fA-F]{6}$/.test( h ) ) {
      return null;
    }
    return [
      parseInt( h.slice( 0, 2 ), 16 ),
      parseInt( h.slice( 2, 4 ), 16 ),
      parseInt( h.slice( 4, 6 ), 16 ),
    ];
  }

  // [r,g,b] (0..255) to [hue 0..359, saturation 0..100, value 0..100].
  private rgbToHsv( rgb: [ number, number, number ] ): [ number, number, number ] {
    const r = rgb[ 0 ] / 255, g = rgb[ 1 ] / 255, b = rgb[ 2 ] / 255;
    const mx = Math.max( r, g, b ), mn = Math.min( r, g, b ), d = mx - mn;
    let h = 0;
    if ( d !== 0 ) {
      if ( mx === r ) {
        h = ( ( g - b ) / d ) % 6;
      } else if ( mx === g ) {
        h = ( b - r ) / d + 2;
      } else {
        h = ( r - g ) / d + 4;
      }
      h = ( h * 60 + 360 ) % 360;
    }
    const s = mx === 0 ? 0 : d / mx;
    // Keep saturation and value unrounded so hsvToHex reproduces the exact input hex
    // at the starting position (rounding here shifts e.g. #F94144 to #FA4144). Hue is
    // rounded for the slider; the level readout is rounded in the template via levelLabel.
    return [ Math.round( h ), s * 100, mx * 100 ];
  }

  // Whole-number level for the readout (the level itself stays precise so the starting
  // swatch matches the post color exactly).
  get levelLabel(): number {
    return Math.round( this.level );
  }

  private hsvToHex( h: number, s: number, v: number ): string {
    s /= 100;
    v /= 100;
    const c = v * s;
    const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
    const m = v - c;
    return this.assemble( h, c, x, m );
  }

  private hslToHex( h: number, s: number, l: number ): string {
    s /= 100;
    l /= 100;
    const c = ( 1 - Math.abs( 2 * l - 1 ) ) * s;
    const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
    const m = l - c / 2;
    return this.assemble( h, c, x, m );
  }

  // Shared sector assembly for both HSV and HSL (they differ only in c, x, m above).
  private assemble( h: number, c: number, x: number, m: number ): string {
    let r = 0, g = 0, b = 0;
    if ( h < 60 ) { r = c; g = x; }
    else if ( h < 120 ) { r = x; g = c; }
    else if ( h < 180 ) { g = c; b = x; }
    else if ( h < 240 ) { g = x; b = c; }
    else if ( h < 300 ) { r = x; b = c; }
    else { r = c; b = x; }
    const to = ( val: number ) => Math.round( ( val + m ) * 255 ).toString( 16 ).padStart( 2, '0' );
    return ( '#' + to( r ) + to( g ) + to( b ) ).toUpperCase();
  }
}
